import { MapPin, Phone, Mail } from "lucide-react";
import React from "react";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import { motion } from "framer-motion";
import { useLanguage } from "../../hooks/useLanguage";

const openingHours = {
  en: [
    { day: "Mon", hours: "10:30 - 23:00 ", isSpecial: false },
    { day: "Tue", hours: "10:30  - 23:00 ", isSpecial: false },
    { day: "Wed", hours: "10:30  - 23:00 ", isSpecial: false },
    { day: "Thu", hours: "10:30  - 23:00 ", isSpecial: false },
    { day: "Fri", hours: "10:30  - 23:00 ", isSpecial: false },
    { day: "Sat", hours: "12:00  - 23:00 ", isSpecial: false },
    { day: "Sun", hours: "12:00  - 23:00 ", isSpecial: true },

  ],
  fi: [
    { day: "Ma", hours: "10:30 - 23:00", isSpecial: false },
    { day: "Ti", hours: "10:30 - 23:00", isSpecial: false },
    { day: "Ke", hours: "10:30 - 23:00", isSpecial: false },
    { day: "To", hours: "10:30 - 23:00", isSpecial: false },
    { day: "Pe", hours: "10:30 - 23:00", isSpecial: false },
    { day: "La", hours: "12:00 - 23:00", isSpecial: false },
    { day: "Su", hours: "12:00 - 23:00", isSpecial: true },
  
  ],
};

export default function Footer() {
  const { t, language } = useLanguage();

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
  };

  return (
    <footer
      id="contact"
      className="bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 text-gray-700 dark:text-gray-400 py-20"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div id="about" className="mb-12">
          {/* Empty div to serve as anchor for about section */}
        </div>

        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-3 gap-12"
        >
          <motion.div
            variants={fadeInUp}
            className="glass-card p-6 rounded-xl shadow-md"
          >
            <h3 className="text-2xl font-semibold mb-6 text-primary dark:text-secondary">
              {t("footer.aboutUsTitle")}
            </h3>
            <p className="text-gray-700 dark:text-gray-300 mb-6">
              {t("footer.aboutUsContent")}
            </p>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="glass-card p-6 rounded-xl shadow-md"
          >
            <h3 className="text-2xl font-semibold mb-6 text-primary dark:text-secondary">
              {t("footer.openingHoursTitle")}
            </h3>
            <div className="space-y-2">
              {openingHours[language as keyof typeof openingHours].map(
                (item, index) => (
                  <div
                    key={index}
                    className={`flex justify-between py-3 px-4 rounded-lg border transition-colors duration-200`}
                  >
                    <span
                      className={`font-bold `}
                    >
                      {item.day}
                    </span>
                    <span
                      className={
                        item.isSpecial
                          ? "text-amber-700 dark:text-amber-400 font-medium"
                          : "text-primary dark:text-secondary"
                      }
                    >
                      {item.hours}
                    </span>
                  </div>
                )
              )}
            </div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            className="glass-card p-6 rounded-xl shadow-md"
          >
            <h3 className="text-2xl font-semibold mb-6 text-primary dark:text-secondary">
              {t("footer.contactInfoTitle")}
            </h3>
            <ul className="space-y-4">
              <li className="flex items-center hover-lift">
                <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-secondary/10 flex items-center justify-center mr-3">
                  <MapPin className="h-5 w-5 text-primary dark:text-secondary" />
                </div>
                <span>{t("footer.address")}</span>
              </li>
              <li className="flex items-center hover-lift">
                <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-secondary/10 flex items-center justify-center mr-3">
                  <Phone className="h-5 w-5 text-primary dark:text-secondary" />
                </div>
                <span>{t("footer.phone")}</span>
              </li>
              <li className="flex items-center hover-lift">
                <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-secondary/10 flex items-center justify-center mr-3">
                  <Mail className="h-5 w-5 text-primary dark:text-secondary" />
                </div>
                <span>{t("footer.email")}</span>
              </li>
            </ul>
            <div className="mt-8 flex space-x-4">
              <a
                href="https://www.facebook.com/profile.php?id=61572269365577"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary/10 dark:bg-secondary/10 flex items-center justify-center text-primary dark:text-secondary hover:bg-primary hover:text-white dark:hover:bg-secondary transition-colors duration-300"
              >
                <FaFacebookF className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/wave_of_flavors/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary/10 dark:bg-secondary/10 flex items-center justify-center text-primary dark:text-secondary hover:bg-primary hover:text-white dark:hover:bg-secondary transition-colors duration-300"
              >
                <FaInstagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-primary/10 dark:bg-secondary/10 flex items-center justify-center text-primary dark:text-secondary hover:bg-primary hover:text-white dark:hover:bg-secondary transition-colors duration-300"
              >
                <FaLinkedinIn className="h-5 w-5" />
              </a>
            </div>
          </motion.div>
        </motion.div>

        <div className="mt-16 text-center">
          <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto mb-6 rounded-full"></div>
          <p className="text-gray-700 dark:text-gray-400">
            {t("footer.rights")}{" "}
            <a
              href="https://bikashdev-com.vercel.app/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary dark:text-secondary hover:text-secondary dark:hover:text-primary font-semibold transition-colors duration-300"
            >
              @BikashDev
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
